// =====================================================================
//  WebIdeal Design System
// ---------------------------------------------------------------------
//  Shared design language for the entire app (marketing site, ATS,
//  HRMS, Jobs, Recruit). Forest-green technical-blueprint base, with
//  restrained skeuomorphism, liquid-glass surfaces, and spatial UI
//  applied where it adds clarity.
//
//  Use the tokens (`colors`, `C`) for colors, the components below for
//  layout primitives, and the CSS utility classes (see blueprint.css)
//  for surface effects (`.ds-spatial`, `.ds-glass`, `.ds-skeu-*`).
// =====================================================================

import React from "react";
import "./blueprint.css";

// ---------------------------------------------------------------------
//  TOKENS
// ---------------------------------------------------------------------
export const colors = {
  paper: "#F7F7F5",
  forest: "#1A3C2B",
  forestSoft: "rgba(26,60,43,0.08)",
  grid: "#3A3A38",
  hairline: "rgba(58,58,56,0.20)",
  mint: "#9EFFBF",
  coral: "#FF8C69",
  gold: "#F4D35E",
  // Brand colors per product module
  product: {
    jobs:    "#0044CC",
    recruit: "#00AAFF",
    ats:     "#6366F1",
    hrms:    "#0EA5A4",
  },
};

// short alias for terse destructure usage
export const C = colors;

// ---------------------------------------------------------------------
//  TECH LABEL, 10px uppercase tracked, monospace feel
// ---------------------------------------------------------------------
export function TechLabel({ children, className = "", style = {}, as: As = "span" }) {
  return (
    <As
      className={`text-[10px] font-bold uppercase ${className}`}
      style={{
        letterSpacing: "0.18em",
        fontVariantNumeric: "tabular-nums",
        ...style,
      }}
    >
      {children}
    </As>
  );
}

// ---------------------------------------------------------------------
//  STATUS BADGE, square dot + tracked label in a hairline border
// ---------------------------------------------------------------------
export function StatusBadge({ children, color = colors.forest, className = "", style = {} }) {
  return (
    <span
      className={`inline-flex items-center gap-2 border px-3 py-1 ${className}`}
      style={{ borderColor: `${color}33`, color, ...style }}
    >
      <span
        className="w-2 h-2"
        style={{ background: color }}
        aria-hidden="true"
      />
      <TechLabel>{children}</TechLabel>
    </span>
  );
}

// ---------------------------------------------------------------------
//  CORNER MARKERS, L-shapes in each corner of a relative parent
// ---------------------------------------------------------------------
export function CornerMarkers({ color = colors.forest, size = 10 }) {
  const s = `${size}px`;
  const base = {
    position: "absolute",
    width: s,
    height: s,
    borderColor: color,
  };
  return (
    <>
      <span aria-hidden="true" className="border-l-2 border-t-2" style={{ ...base, top: 0, left: 0 }} />
      <span aria-hidden="true" className="border-r-2 border-t-2" style={{ ...base, top: 0, right: 0 }} />
      <span aria-hidden="true" className="border-l-2 border-b-2" style={{ ...base, bottom: 0, left: 0 }} />
      <span aria-hidden="true" className="border-r-2 border-b-2" style={{ ...base, bottom: 0, right: 0 }} />
    </>
  );
}

// ---------------------------------------------------------------------
//  PAGE CONTAINER, consistent max-width + horizontal padding
// ---------------------------------------------------------------------
export function PageContainer({ children, className = "" }) {
  return (
    <div className={`max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 ${className}`}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------
//  SECTION SHELL, paper-bg + bottom hairline
// ---------------------------------------------------------------------
export function Section({ children, className = "", style = {}, dark = false }) {
  return (
    <section
      className={`relative py-20 md:py-24 border-b ${className}`}
      style={{
        borderColor: colors.hairline,
        background: dark ? colors.forest : "transparent",
        color: dark ? colors.paper : "inherit",
        ...style,
      }}
    >
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------
//  SECTION HEADER, numbered status badge + heading + subhead + meta
// ---------------------------------------------------------------------
export function SectionHeader({
  index,
  badge,
  title,
  subtitle,
  meta,
  className = "",
  dark = false,
}) {
  const labelColor = dark ? colors.mint : colors.forest;
  const subColor = dark ? "rgba(247,247,245,0.75)" : colors.grid;
  return (
    <div className={`mb-12 flex items-end justify-between flex-wrap gap-4 ${className}`}>
      <div className="max-w-2xl">
        {(badge || index) && (
          <StatusBadge color={labelColor}>
            {index ? `${index} · ${badge || ""}`.trim().replace(/·\s*$/, "").trim() : badge}
          </StatusBadge>
        )}
        {title && (
          <h2
            className="mt-4 text-4xl md:text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight"
            style={{ color: dark ? colors.paper : colors.forest }}
          >
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="mt-4 text-lg" style={{ color: subColor }}>
            {subtitle}
          </p>
        )}
      </div>
      {meta && (
        <TechLabel style={{ color: dark ? "rgba(158,255,191,0.7)" : `${colors.grid}99` }}>
          {meta}
        </TechLabel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
//  BUTTONS
// ---------------------------------------------------------------------
export function PrimaryButton({ children, className = "", style = {}, ...rest }) {
  return (
    <button
      type="button"
      className={`ds-skeu-btn-primary cursor-pointer h-12 px-7 text-sm font-semibold inline-flex items-center justify-center ${className}`}
      style={style}
      {...rest}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, className = "", style = {}, ...rest }) {
  return (
    <button
      type="button"
      className={`cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center justify-center group transition-colors duration-150 hover:bg-[#1A3C2B]/[0.04] ${className}`}
      style={{ borderColor: colors.forest, color: colors.forest, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------
//  GLASS CARD, frosted translucent surface (liquid glass)
// ---------------------------------------------------------------------
export function GlassCard({ children, className = "", style = {}, dark = false }) {
  return (
    <div
      className={`${dark ? "ds-glass-dark" : "ds-glass"} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------
//  BLUEPRINT CARD, paper card with accent left-border + hairline
// ---------------------------------------------------------------------
export function BlueprintCard({
  children,
  accent = colors.forest,
  className = "",
  style = {},
  spatial = false,
}) {
  return (
    <div
      className={`relative p-7 md:p-8 transition-colors duration-200 ${spatial ? "ds-spatial" : ""} ${className}`}
      style={{
        background: colors.paper,
        borderLeft: `4px solid ${accent}`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------
//  SKEUOMORPHIC INPUT, softly inset, paper background
// ---------------------------------------------------------------------
export function SkeuInput({ className = "", style = {}, ...rest }) {
  return (
    <input
      className={`ds-skeu-input w-full h-11 px-3 text-sm focus:outline-none ${className}`}
      style={{ color: colors.forest, fontFamily: "inherit", ...style }}
      {...rest}
    />
  );
}

export function SkeuTextarea({ className = "", style = {}, rows = 4, ...rest }) {
  return (
    <textarea
      rows={rows}
      className={`ds-skeu-input w-full px-3 py-2.5 text-sm focus:outline-none resize-y ${className}`}
      style={{ color: colors.forest, fontFamily: "inherit", ...style }}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------------
//  SKEUOMORPHIC CHECKBOX, square, inset box with an accent-fill check
//  state. Matches the blueprint input/button language. Pass `label` for
//  a built-in caption (e.g. "Remember me").
// ---------------------------------------------------------------------
export function SkeuCheckbox({
  checked,
  onChange,
  label,
  id,
  accent = colors.forest,
  className = "",
  style = {},
  labelStyle = {},
  ...rest
}) {
  return (
    <label
      htmlFor={id}
      className={`ds-skeu-checkbox inline-flex items-center gap-2.5 cursor-pointer select-none group ${className}`}
      style={style}
    >
      <span className="relative inline-flex items-center justify-center w-[18px] h-[18px] flex-shrink-0">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="ds-skeu-checkbox-input"
          {...rest}
        />
        <span
          className="ds-skeu-checkbox-box"
          style={checked ? { background: `linear-gradient(180deg, ${accent}e6 0%, ${accent} 100%)`, borderColor: accent } : undefined}
        >
          <svg viewBox="0 0 16 16" className="ds-skeu-checkbox-mark" aria-hidden="true" style={{ opacity: checked ? 1 : 0, transform: checked ? "scale(1)" : "scale(0.5)" }}>
            <path d="M3.5 8.3L6.4 11.1L12.6 4.7" fill="none" stroke="#F7F7F5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </span>
      {label && (
        <span className="text-[13px] font-semibold" style={{ color: colors.grid, ...labelStyle }}>
          {label}
        </span>
      )}
    </label>
  );
}

// ---------------------------------------------------------------------
//  MOTION PRESETS, for use with framer-motion's `variants` prop
// ---------------------------------------------------------------------
export const motionFadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.06, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

export const motionStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

/**
 * Convenience namespace so callers can `import { motion as M }` and use
 * `M.variants.fadeUp` / `M.variants.stagger` without juggling individual
 * named imports.
 */
export const motion = {
  variants: {
    fadeUp: motionFadeUp,
    stagger: motionStagger,
  },
};
